import React, { useEffect, useMemo, useRef, useState } from "react";
import { AxiosResponse } from "axios";
import { CurrentUserResource } from "../../stores/CurrentUserStore/Types";
import { getRequest, postRequest } from "../../utils/httpRequest";

// Everything below used to be a hard-coded tenth birthday campaign — the copy,
// the code 4224, the cake, the end date, all of it in this file. It now comes
// from Site Content → Website Banner, so a campaign is a form rather than a
// deploy.

interface Banner {
  content: string;
  checkout_content: string;
  action_type: "code" | "link";
  voucher_code: string | null;
  url: string | null;
  link_label: string;
  icon: string | null;
  ends_at: string | null;
  background_color: string;
  text_color: string;
  accent_color: string;
  highlight_color: string;
  festive_icon: "cake" | "towel" | null;
}

interface Props {
  currentUser: CurrentUserResource;
  pageContext: "homepage" | "course" | "logged-in" | "checkout";
}

// The tenth anniversary cake, kept because the anniversary runs all year and
// the candle is genuinely nice. Opt-in per campaign rather than always there:
// a cake can only ever mean one thing.
const CakeIcon = ({ candle = true }: { candle?: boolean }) => (
  <svg
    viewBox="0 0 26 26"
    aria-hidden="true"
    style={{ width: "100%", height: "100%" }}
  >
    {candle && (
      <>
        <path
          className="flame"
          d="M13 2.4c1.7 1.8 2.4 3 2.4 4.2a2.4 2.4 0 0 1-4.8 0c0-1.1.7-2.3 2.4-4.2z"
          fill="#F5B041"
          stroke="#C4841D"
          strokeWidth=".7"
        />
        <rect
          x="12.1"
          y="7"
          width="1.8"
          height="4"
          fill="#FFA5BC"
          stroke="#C4841D"
          strokeWidth=".5"
        />
      </>
    )}
    <path
      d="M4.5 12.5c1.4-1.6 2.8 0 4.2-1.4s2.8 1.4 4.3 0 2.8 1.4 4.2 0 2.9 1.6 4.3 0v3H4.5z"
      fill={candle ? "#F5F0E1" : "#2E2437"}
      fillOpacity={candle ? "1" : ".08"}
      stroke={candle ? "#C4841D" : "#2E2437"}
      strokeWidth={candle ? "1" : "1.1"}
      strokeLinejoin="round"
    />
    <rect
      x="4.5"
      y="14.5"
      width="17"
      height="7.5"
      rx="1.5"
      fill={candle ? "#F5B041" : "none"}
      fillOpacity=".3"
      stroke={candle ? "#C4841D" : "#2E2437"}
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    {!candle && (
      <>
        <rect x="12.1" y="7.5" width="1.8" height="4" fill="#2E2437" />
        <circle cx="13" cy="5.6" r="1.5" fill="#2E2437" />
      </>
    )}
    {candle && (
      <ellipse
        cx="13"
        cy="22.4"
        rx="11"
        ry="1.7"
        fill="none"
        stroke="#C4841D"
        strokeWidth="1.2"
        opacity=".55"
      />
    )}
  </svg>
);

// A towel for Towel Day, 25 May — the one thing a hitchhiker must never be
// without. Hung on a rail, because a folded towel reads as a flannel at 26px.
const TowelIcon = () => (
  <svg
    viewBox="0 0 26 26"
    aria-hidden="true"
    style={{ width: "100%", height: "100%" }}
  >
    <rect x="3" y="5" width="20" height="1.8" rx=".9" fill="#C4841D" opacity=".85" />
    <path
      d="M8 6.2h10a1 1 0 0 1 1 1v11.5a2.3 2.3 0 0 1-2.3 2.3H9.3A2.3 2.3 0 0 1 7 18.7V7.2a1 1 0 0 1 1-1z"
      fill="#FBFAF6"
      stroke="#B9B2A4"
      strokeWidth="1.1"
      strokeLinejoin="round"
    />
    <rect x="7" y="10.8" width="12" height="1.9" fill="#3E96C9" />
    <rect x="7" y="13.8" width="12" height="1.2" fill="#7FC7E8" />
    <path d="M13 6.2v15" stroke="#E6E0D2" strokeWidth=".9" />
  </svg>
);

const CopyIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
  </svg>
);

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

// The anniversary bar picked out its numbers in yellow — "we flipped *42* to
// give everyone *24% off*" — and that highlighting is most of why it read as
// designed rather than typed. Same asterisk convention as the popup heading, so
// a campaign keeps it without needing a developer.
const withHighlight = (text: string, colour?: string) =>
  text
    .split(/\*([^*]+)\*/)
    .map((part, index) =>
      index % 2 === 1 ? (
        <span className="lo" key={index} style={colour ? { color: colour } : undefined}>
          {part}
        </span>
      ) : (
        part
      ),
    );

// A full address to somewhere else entirely, as opposed to "/courses/x" or a
// link back to 42courses. Anything unparseable is treated as internal, which is
// the safer default: it stays in this tab rather than opening a stray window.
const isExternal = (url: string | null) => {
  if (!url) return false;

  try {
    return new URL(url, window.location.origin).origin !== window.location.origin;
  } catch (error) {
    return false;
  }
};

// "Ends 31 July" — the date the bar shows is the same one that switches it off,
// rather than a second date typed separately and left to drift.
// Short months keep the deadline from crowding the message: "Ends 4 Sept"
// rather than "Ends 4 September". May, June and July are already short enough
// that abbreviating them would look like a typo, so they are left alone.
const MONTHS = [
  "Jan", "Feb", "March", "April", "May", "June",
  "July", "Aug", "Sept", "Oct", "Nov", "Dec",
];

const deadlineLabel = (endsAt: string | null) => {
  if (!endsAt) return null;

  const date = new Date(endsAt);
  if (isNaN(date.getTime())) return null;

  return `Ends ${date.getDate()} ${MONTHS[date.getMonth()]}`;
};

const PromoBar: React.FC<Props> = ({ currentUser, pageContext }) => {
  const [banner, setBanner] = useState<Banner | null>(null);
  const [dismissalKey, setDismissalKey] = useState("");
  const [dismissed, setDismissed] = useState(false);
  const [codeLabel, setCodeLabel] = useState("");
  const [cakeOut, setCakeOut] = useState(false);
  // The dismissal is on a short delay so "Copied" can be read first. Held in a
  // ref so it can be cancelled if the visitor navigates away in that gap.
  const dismissTimer = useRef<number>();

  useEffect(() => () => window.clearTimeout(dismissTimer.current), []);

  useEffect(() => {
    getRequest("/api/announcements.json")
      .then((response: AxiosResponse<any>) => {
        const { data } = response;
        if (!data.show_announcement || !data.banner) return;

        setBanner(data.banner);
        setDismissalKey(data.banner_id);
        setCodeLabel(
          data.banner.voucher_code ? `Code: ${data.banner.voucher_code}` : "",
        );
      })
      .catch(() => {
        // A promotion is never worth an error message. Stay quiet.
      });
  }, []);

  // The rest of the page is laid out under a nav of a known height, so the bar
  // has to tell it how much taller things just got. The body class and
  // --promo-h are what let the course page's gift and price bar sit underneath
  // this one instead of behind it.
  useEffect(() => {
    const showing = !!banner && !dismissed;

    const measure = () => {
      const nav = document.querySelector<HTMLElement>(
        ".top-nav, .checkout-nav",
      );
      if (nav) {
        document.documentElement.style.setProperty(
          "--nav-h",
          `${nav.offsetHeight}px`,
        );
      }

      // Deliberately not --promo-h: that one already feeds the bar's own
      // min-height, so writing a measured height back into it makes the bar
      // grow by its own size on every measure.
      const bar = document.querySelector<HTMLElement>(".promo-bar");
      document.documentElement.style.setProperty(
        "--promo-offset",
        showing && bar ? `${bar.offsetHeight}px` : "0px",
      );
    };

    document.body.classList.toggle("has-promo-bar", showing);
    measure();
    window.addEventListener("resize", measure);

    return () => {
      window.removeEventListener("resize", measure);
      document.body.classList.remove("has-promo-bar");
    };
  }, [banner, dismissed]);

  const message = useMemo(() => {
    if (!banner) return "";

    return pageContext === "checkout" ? banner.checkout_content : banner.content;
  }, [banner, pageContext]);

  // No checkout message means no bar at checkout. Nothing is more expensive to
  // interrupt than a payment in progress.
  const silentAtCheckout = pageContext === "checkout" && !banner?.checkout_content;

  const dismiss = () => {
    setDismissed(true);

    postRequest("/api/announcements.json", { banner_id: dismissalKey }).catch(
      (error) => {
        (window as any).honeybadger?.notify(
          error?.response?.data?.error || error,
        );
      },
    );
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || dismissed || !banner) return;

      dismiss();
    };

    document.addEventListener("keydown", handleEscape);

    return () => document.removeEventListener("keydown", handleEscape);
  }, [dismissed, banner, dismissalKey]);

  const copyCode = () => {
    if (!banner?.voucher_code) return;

    const code = banner.voucher_code;

    // Says "Copied" where the eye already is, then gets out of the way. The
    // toast this replaced appeared at the very bottom of the page, far from the
    // button that triggered it and gone before it could be read.
    const onCopied = () => {
      setCodeLabel("Copied");
      dismissTimer.current = window.setTimeout(dismiss, 900);
    };

    // Only on a successful copy. Dismissing on a failed one would take the bar
    // away and leave the visitor with nothing on the clipboard and no way back
    // to the code — the clipboard API rejects on an insecure origin or without
    // permission, and execCommand returns false rather than throwing.
    const onCopyFailed = () => {
      setCodeLabel(`Code: ${code}`);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(onCopied, onCopyFailed);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = code;
    document.body.appendChild(textarea);
    textarea.select();

    let copied = false;
    try {
      copied = document.execCommand("copy");
    } catch (_error) {
      copied = false;
    }
    document.body.removeChild(textarea);

    copied ? onCopied() : onCopyFailed();
  };

  // Blowing out the candle copies the code too, exactly as the anniversary bar
  // did — the cake was the fun way to take the offer, not decoration.
  const blowOutCandle = () => {
    if (cakeOut) return;

    setCakeOut(true);
    if (banner?.action_type === "code") copyCode();
  };

  // Only voucher bars are hidden from people already on a plan. A link banner
  // is usually a new course or an announcement, which is exactly the thing a
  // subscriber should hear about first.
  const hasActiveSubscription =
    currentUser.kind === "current-user" && currentUser.has_active_subscription;
  const offeringNothingNew =
    hasActiveSubscription && banner?.action_type === "code";

  if (!banner || dismissed || offeringNothingNew || silentAtCheckout) {
    return null;
  }

  const deadline = deadlineLabel(banner.ends_at);

  return (
    <>
      <div
        className="promo-bar"
        id="promoBar"
        role="region"
        aria-label="Offer"
        aria-live="polite"
        style={{ background: banner.background_color, color: banner.text_color }}
      >
        <div className="pb-inner">
          {banner.festive_icon === "cake" ? (
            <button
              className={`cake ${cakeOut ? "is-out" : ""}`}
              type="button"
              aria-label={cakeOut ? "Candle blown out" : "Blow out the candle"}
              title="Go on, blow it out."
              onClick={blowOutCandle}
            >
              <CakeIcon candle={!cakeOut} />
            </button>
          ) : banner.festive_icon === "towel" ? (
            <span className="cake" aria-hidden="true" title="Don't panic.">
              <TowelIcon />
            </span>
          ) : (
            banner.icon && (
              <span className="pb-icon" aria-hidden="true">
                {banner.icon}
              </span>
            )
          )}
          <span className="pb-text" id="pbText">
            {withHighlight(message, banner.highlight_color)}
          </span>

          {banner.action_type === "code" ? (
            <button
              className="pb-code"
              type="button"
              aria-label={`Copy discount code ${banner.voucher_code}`}
              onClick={copyCode}
              // Inverted against the bar rather than tinted with it: the pill's
              // whole job is to stand out, and taking the bar's own text colour
              // would paint it onto a background of the same shade.
              style={{
                background: banner.text_color,
                color: banner.background_color,
                borderColor: banner.text_color,
              }}
            >
              <CopyIcon />
              <span id="pbCodeLabel">{codeLabel}</span>
            </button>
          ) : (
            <a
              className="pb-link"
              href={banner.url || "#"}
              // External campaigns open in a new tab, so nobody is navigated off
              // the site by a promotion. Internal ones stay in this tab and
              // dismiss on the way, so the bar is gone when the page they asked
              // for arrives.
              target={isExternal(banner.url) ? "_blank" : undefined}
              rel={isExternal(banner.url) ? "noopener noreferrer" : undefined}
              onClick={dismiss}
              style={{
                background: banner.text_color,
                color: banner.background_color,
                borderColor: banner.text_color,
              }}
            >
              {banner.link_label} &rarr;
            </a>
          )}

          {deadline && <span className="pb-deadline">{deadline}</span>}
        </div>
        <button
          className="pb-close"
          id="pbClose"
          type="button"
          aria-label="Dismiss this offer"
          onClick={dismiss}
          style={{ color: banner.text_color }}
        >
          <CloseIcon />
        </button>
      </div>
    </>
  );
};

export default PromoBar;
